// started as rebass copy pasta
// curious if we could just extend the rebass base elements instead
import styled from '@emotion/styled'
import isPropValid from '@emotion/is-prop-valid'

import {
  space,
  color,
  width,
  maxWidth,
  height,
  flex,
  order,
  alignSelf,
  flexWrap,
  flexDirection,
  alignItems,
  justifyContent,
  fontSize,
  fontFamily,
  fontWeight,
  textAlign,
  lineHeight,
  letterSpacing,
  borders,
  borderColor,
  borderRadius,
  buttonStyle,
  fontStyle,
  display,
  position,
  boxShadow,
  themeGet,
} from 'styled-system'

const themed = key => props => props.theme[key]

const shouldForwardProp = propTypes => prop =>
  isPropValid(prop) && !Object.keys(propTypes).includes(prop)

const boxPropTypes = {
  ...alignSelf.propTypes,
  ...borderRadius.propTypes,
  ...color.propTypes,
  ...display.propTypes,
  ...flex.propTypes,
  ...fontSize.propTypes,
  ...height.propTypes,
  ...order.propTypes,
  ...position.propTypes,
  ...space.propTypes,
  ...width.propTypes,
  ...maxWidth.propTypes,
  ...textAlign.propTypes,
  ...boxShadow.propTypes,
  ...flexWrap.propTypes,
}

export const Box = styled('div', {
  shouldForwardProp: shouldForwardProp(boxPropTypes),
})(
  {
    boxSizing: `border-box`,
  },
  alignSelf,
  borderRadius,
  color,
  display,
  flex,
  height,
  fontSize,
  maxWidth,
  order,
  position,
  space,
  textAlign,
  width,
  boxShadow,
  flexWrap
)

Box.propTypes = boxPropTypes

export const Flex = styled(Box)(
  {
    display: `flex`,
  },
  alignItems,
  justifyContent,
  order,
  flexDirection,
  flexWrap
)

Flex.propTypes = {
  ...alignItems.propTypes,
  ...flexDirection.propTypes,
  ...flexWrap.propTypes,
  ...justifyContent.propTypes,
  ...order.propTypes,
}

const textPropTypes = {
  ...fontFamily.propTypes,
  ...fontStyle.propTypes,
  ...fontWeight.propTypes,
  ...letterSpacing.propTypes,
  ...lineHeight.propTypes,
  ...textAlign.propTypes,
}

export const Text = styled(Box, {
  shouldForwardProp: shouldForwardProp(textPropTypes),
})(fontFamily, fontStyle, fontWeight, letterSpacing, lineHeight, textAlign)

Text.propTypes = textPropTypes

Text.defaultProps = {
  color: 'grey.2',
}

export const Heading = styled(Text)({
  textRendering: 'optimizeLegibility',
})

Heading.defaultProps = {
  as: `h1`,
  color: 'grey.900',
  fontSize: 7,
  lineHeight: 'title',
  letterSpacing: 'tight',
  m: 0,
}

export const Link = styled(Box)(themed(`Link`))

Link.defaultProps = {
  as: `a`,
  color: `blue`,
}

const buttonPropTypes = {
  ...borders.propTypes,
  ...borderColor.propTypes,
  ...borderRadius.propTypes,
  ...fontFamily.propTypes,
  ...fontWeight.propTypes,
  ...buttonStyle.propTypes,
}

export const Button = styled(Box, {
  shouldForwardProp: shouldForwardProp(buttonPropTypes),
})(
  {
    appearance: `none`,
    display: `inline-block`,
    lineHeight: `inherit`,
    textAlign: `center`,
    textDecoration: `none`,
    WebkitFontSmoothing: 'antialiased',
  },
  borders,
  borderColor,
  borderRadius,
  buttonStyle,
  fontFamily,
  fontSize,
  fontWeight,
  props =>
    props.outlined && {
      background: themeGet('colors.white.900')(props),
      border: `1px solid ${themeGet('colors.purple.300')(props)}`,
      color: themeGet('colors.purple.600')(props),
      fontSize: `${themeGet('fontSizes.3')(props)}px`,
      fontWeight: 'normal',
      padding: `${themeGet('space.2')(props)}px ${themeGet('space.3')(
        props
      )}px`,
      [`@media (min-width: ${themeGet('breakpoints.md')(props)}px)`]: {
        fontSize: `${themeGet('fontSizes.3')(props)}px`,
      },
      svg: {
        height: 20,
      },
    }
)

Button.propTypes = buttonPropTypes

Button.defaultProps = {
  as: `button`,
  bg: `purple.600`,
  border: 0,
  borderRadius: 2,
  color: `white.900`,
  fontFamily: 'brand',
  fontSize: { xxs: 4, md: 5 },
  fontWeight: 1,
  m: 0,
  px: 3,
  py: 2,
}
